import React from 'react';
import cx from 'classnames';
import tinycolor from 'tinycolor2';

import { FontAwesomeIcon } from '@magoosh/lib/icons';

import { Series } from '../types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props extends Series {
  active?: boolean;
  compact?: boolean;
  colorPalette: {
    dark: string;
    light: string;
  };
  onSelected: () => void;
  subtext?: string;
}

const SeriesCard: React.FC<Props> = (props) => {
  if (props.score) {
    return <SeriesCardWithScore {...props} />;
  } else {
    return <SeriesCardWithoutScore {...props} />
  }
};

const SeriesCardWithoutScore: React.FC<Props> = (props) => {
  return (
    <div className="flex-1">
      <div
        className={cx(style.seriesCard, 'align-items-center d-flex flex-row gap-s', { [style.compact]: props.compact })}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 8,
          transition: 'background-color 250ms ease-in, color 250ms ease-in'
        }}
      >
        <div className="hidden-xs">
          <div
            style={{
              backgroundColor: colors.grayLighter,
              borderColor: '#E7E7E7',
              borderRadius: '50%',
              borderStyle: 'solid',
              borderWidth: 1,
              height: props.compact ? 14 : 16,
              width: props.compact ? 14 : 16
            }}
          />
        </div>

        <div className="d-flex flex-1 flex-column gap-xxs">
          <div className="d-flex flex-row gap-s">
            <div
              className="flex-none text-bold text-white"
              style={{
                backgroundColor: props.colorPalette.dark,
                borderRadius: 20,
                color: props.colorPalette.light,
                fontSize: props.compact ? 14 : 16,
                padding: '0 10px'
              }}
            >
              {props.name}
            </div>

            {
              props.target && !props.compact && (
                <div className="align-items-center d-flex flex-md-1 justify-content-center justify-content-md-end small text-bold gap-xxs">
                  <FontAwesomeIcon icon="bullseye" style={{ color: props.colorPalette.light }} />
                  {' '}<span style={{ color: props.colorPalette.light }}>{props.target}</span>
                </div>
              )
            }
          </div>

          <div className="d-flex flex-1 flex-column gap-xxs">
            <div
              className={cx('text-bold u-margin-A-n', { h2: !props.compact, h3: props.compact })}
              style={{ color: props.colorPalette.dark }}
            >
              N/A
            </div>

            {
              props.subtext && (
                <div
                  className="flex-1 small text-gray-light"
                  style={{
                    fontSize: props.compact ? 12 : 14,
                    lineHeight: 1.25
                  }}
                >
                  {props.subtext}
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

const SeriesCardWithScore: React.FC<Props> = (props) => {
  return (
    <div className="flex-1">
      <div
        className={cx(style.seriesCard, 'align-items-center d-flex flex-row gap-s', { [style.active]: props.active, [style.compact]: props.compact })}
        onClick={props.onSelected}
        style={{
          backgroundColor: props.active ? props.colorPalette.dark : '#ffffff',
          borderRadius: 8,
          cursor: 'pointer',
          transition: 'background-color 250ms ease-in, color 250ms ease-in'
        }}
      >
        <div className="hidden-xs">
          {
            props.active ? (
              <div
                className="align-items-center d-flex justify-content-center"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  height: props.compact ? 14 : 16,
                  width: props.compact ? 14 : 16
                }}
              >
                <FontAwesomeIcon
                  icon="check"
                  style={{ color: props.colorPalette.dark, fontSize: 12, WebkitTextStroke: '1px #ffffff' }}
                />
              </div>
            ) : (
              <div
                style={{
                  borderColor: '#BBB7C0',
                  borderRadius: '50%',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  height: props.compact ? 14 : 16,
                  width: props.compact ? 14 : 16
                }}
              />
            )
          }
        </div>

        <div className="d-flex flex-1 flex-column gap-xs">
          <div className="d-flex flex-row gap-s">
            {
              props.active ? (
                <div
                  className="text-bold text-white"
                  style={{
                    backgroundColor: tinycolor(props.colorPalette.dark).lighten(5).toString(),
                    borderRadius: 20,
                    color: props.colorPalette.light,
                    fontSize: props.compact ? 14 : 16,
                    padding: '0 10px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {props.name}
                </div>
              ): (
                <div
                  className="text-bold text-white"
                  style={{
                    backgroundColor: props.colorPalette.dark,
                    borderRadius: 20,
                    color: props.colorPalette.light,
                    fontSize: props.compact ? 14 : 16,
                    padding: '0 10px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {props.name}
                </div>
              )
            }

            {
              props.target && !props.compact && (
                <div className="align-items-center d-flex flex-md-1 justify-content-center justify-content-lg-end small text-bold gap-xxs">
                  <FontAwesomeIcon icon="bullseye" style={{ color: props.active ? props.colorPalette.light : props.colorPalette.dark }} />
                  {' '}<span style={{ color: props.active ? props.colorPalette.light : props.colorPalette.dark }}>{props.target}</span>
                </div>
              )
            }
          </div>

          <div className="d-flex flex-1 flex-column gap-xxs">
            <div
              className={cx('text-bold u-margin-A-n', { h2: !props.compact, h3: props.compact })}
              style={{
                color: props.active ? props.colorPalette.light : (props.colorPalette?.dark || colors.brandSecondary),
                whiteSpace: 'nowrap'
              }}
            >
              {
                props.score.low === props.score.high ? (
                  props.score.low
                ): (
                  `${props.score.low} - ${props.score.high}`
                )
              }
            </div>

            {
              props.subtext && (
                <div
                  className={cx('flex-1', { 'text-gray-light': !props.active, 'text-white': props.active })}
                  style={{
                    fontSize: props.compact ? 12 : 14,
                    lineHeight: 1.25
                  }}
                >
                  {props.subtext}
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesCard;
